import { Injectable, inject } from '@angular/core';
import {
	HttpRequest,
	HttpEvent,
	HttpErrorResponse,
	HttpInterceptorFn,
	HttpHandlerFn,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { AuthService } from './auth.service';

// Sentinel emitted to refreshTokenSubject when refresh fails.
// Unblocks any requests that were waiting for a new token so they
// fail immediately instead of hanging until page reload.
const REFRESH_FAILED = '__REFRESH_FAILED__';

// Token refresh state managed via service-scoped singleton to avoid module-level variables
@Injectable({ providedIn: 'root' })
class TokenRefreshState {
	isRefreshing = false;
	// Timestamp until which refresh requests are suppressed after a 429 (ms).
	refreshBackoffUntil = 0;
	// Emits the new access token on success, or REFRESH_FAILED sentinel on error.
	readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
}

// Functional interceptor for Angular 21 standalone approach
export const authInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const authService = inject(AuthService);
	const refreshState = inject(TokenRefreshState);

	// Skip auth header for public endpoints
	if (isPublicEndpoint(req.url)) {
		return next(req);
	}

	// Proactive token refresh — if token is expired/about to expire,
	// refresh BEFORE sending request to prevent 401s from reaching the server.
	// Also handles the case after page reload where accessToken is null but
	// refreshToken exists (avoids 401 console noise on private endpoints).
	if (!authService.getAccessToken() || authService.isTokenExpired()) {
		return handleProactiveRefresh(req, next, authService, refreshState);
	}

	const token = authService.getAccessToken();

	if (token) {
		req = addAuthHeader(req, token);
	}

	return next(req).pipe(
		catchError((error: HttpErrorResponse) => {
			// Handle 401 Unauthorized - try to refresh token
			if (error.status === 401 && !isAuthEndpoint(req.url)) {
				return handle401Error(req, next, authService, refreshState);
			}

			return throwError(() => error);
		}),
	);
};

function handleProactiveRefresh(
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
	authService: AuthService,
	state: TokenRefreshState,
): Observable<HttpEvent<unknown>> {
	// If still within backoff window (previous refresh was rate-limited),
	// fail immediately rather than firing another doomed refresh request.
	if (Date.now() < state.refreshBackoffUntil) {
		return throwError(
			() =>
				new HttpErrorResponse({
					status: 429,
					statusText: 'Too Many Requests — refresh backoff active',
				}),
		);
	}

	if (!state.isRefreshing) {
		state.isRefreshing = true;
		state.refreshTokenSubject.next(null);

		return authService.refreshToken().pipe(
			switchMap((tokenResponse) => {
				state.isRefreshing = false;
				state.refreshTokenSubject.next(tokenResponse.access_token);
				return next(addAuthHeader(req, tokenResponse.access_token));
			}),
			catchError((error: HttpErrorResponse) => {
				state.isRefreshing = false;

				if (error.status === 429) {
					// Back off (server Retry-After, capped) to break the retry loop.
					state.refreshBackoffUntil = Date.now() + refreshBackoffMs(error);
				}

				// Unblock all waiting requests — they will see the sentinel and fail.
				state.refreshTokenSubject.next(REFRESH_FAILED);

				if (error.status === 401 || error.status === 403) {
					authService.forceLogout();
				}

				return throwError(() => error);
			}),
			finalize(() => {
				state.isRefreshing = false;
			}),
		);
	}

	// Another request already triggered refresh — wait for new token (or sentinel).
	return state.refreshTokenSubject.pipe(
		filter((token) => token !== null),
		take(1),
		switchMap((token) => {
			if (token === REFRESH_FAILED) {
				return throwError(
					() => new HttpErrorResponse({ status: 0, statusText: 'Token refresh failed' }),
				);
			}

			return next(addAuthHeader(req, token!));
		}),
	);
}

function handle401Error(
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
	authService: AuthService,
	state: TokenRefreshState,
): Observable<HttpEvent<unknown>> {
	// Respect backoff — don't hammer refresh after a 429.
	if (Date.now() < state.refreshBackoffUntil) {
		return throwError(
			() =>
				new HttpErrorResponse({
					status: 429,
					statusText: 'Too Many Requests — refresh backoff active',
				}),
		);
	}

	if (!state.isRefreshing) {
		state.isRefreshing = true;
		state.refreshTokenSubject.next(null);

		return authService.refreshToken().pipe(
			switchMap((tokenResponse) => {
				state.isRefreshing = false;
				state.refreshTokenSubject.next(tokenResponse.access_token);
				return next(addAuthHeader(req, tokenResponse.access_token));
			}),
			catchError((error: HttpErrorResponse) => {
				state.isRefreshing = false;

				if (error.status === 429) {
					state.refreshBackoffUntil = Date.now() + refreshBackoffMs(error);
				}

				state.refreshTokenSubject.next(REFRESH_FAILED);

				if (error.status === 401 || error.status === 403) {
					authService.forceLogout();
				}

				return throwError(() => error);
			}),
			finalize(() => {
				state.isRefreshing = false;
			}),
		);
	}

	// Wait for token refresh to complete.
	return state.refreshTokenSubject.pipe(
		filter((token) => token !== null),
		take(1),
		switchMap((token) => {
			if (token === REFRESH_FAILED) {
				return throwError(
					() => new HttpErrorResponse({ status: 0, statusText: 'Token refresh failed' }),
				);
			}

			return next(addAuthHeader(req, token!));
		}),
	);
}

// Backoff after a rate-limited (429) refresh: honor the server's Retry-After
// when present (capped), else a short default so authed requests recover quickly.
function refreshBackoffMs(error: HttpErrorResponse): number {
	const retryAfter = Number(error.headers?.get('retry-after'));

	if (Number.isFinite(retryAfter) && retryAfter > 0) {
		return Math.min(retryAfter * 1000, 60_000);
	}

	return 8_000;
}

function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
	return req.clone({
		setHeaders: {
			Authorization: `Bearer ${token}`,
		},
	});
}

export function isPublicEndpoint(url: string): boolean {
	// Skip auth for external APIs (Binance, etc.)
	if (url.includes('binance.com') || url.includes('stream.binance.com')) {
		return true;
	}

	// Skip auth for local assets (translations, fonts, etc.) — anything not going to /api/
	if (!url.includes('/api/')) {
		return true;
	}

	// Only auth-related endpoints are public
	// All market data, patterns, etc. require authentication
	const publicEndpoints = [
		'/auth/login',
		'/auth/register',
		'/auth/refresh',
		'/auth/forgot-password',
		'/auth/reset-password',
		'/auth/verify-email',
		'/auth/resend-verification',
		// Quick-unlock / passkey LOGIN run while logged out — they must skip the auth
		// interceptor, otherwise it fires a doomed token-refresh and the real request is
		// never sent (enroll/register stay private — they require a session).
		'/auth/quick-unlock/login',
		'/auth/webauthn/login',
		// Katalog modułów i cennik Cryptionis Token — czyta go publiczna oferta (bez logowania).
		'/modules/catalog',
		'/health',
		'/meta',
		'/market/tickers/live',
		'/market/overview',
		'/market/candles/latest',
		'/symbols',
	];

	// Dopasowanie PREFIKSU ścieżki za `/api/vN`, nie podciągu: `includes('/symbols')` łapało
	// też `/market/consolidation-index/symbols`, które szło bez tokena i dostawało 401 —
	// lista monet w Konsolidacjach nie wczytywała się nigdy.
	const path = apiPath(url);

	return publicEndpoints.some(
		(endpoint) =>
			path === endpoint || path.startsWith(endpoint + '/') || path.startsWith(endpoint + '?'),
	);
}

/** Ścieżka żądania za `/api/` (i za `/vN`, jeśli jest), np. `/market/overview?x=1`. */
export function apiPath(url: string): string {
	const rest = url.slice(url.indexOf('/api/') + 4).replace(/\/{2,}/g, '/');

	return rest.replace(/^\/v\d+(?=\/)/, '');
}

function isAuthEndpoint(url: string): boolean {
	const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

	return authEndpoints.some((endpoint) => url.includes(endpoint));
}
